const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY?.trim();
const POSTHOG_HOST = (
  import.meta.env.VITE_POSTHOG_HOST?.trim() || "https://us.i.posthog.com"
).replace(/\/+$/, "");

type PostHog = {
  init: (key: string, options: Record<string, unknown>) => void;
  capture: (name: string, params?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    posthog?: PostHog;
  }
}

/**
 * Lo que el panel puede contar de sí mismo sin contar de sus pacientes.
 *
 * La landing puede medirse entera: es una página pública. Acá no. Las
 * direcciones del panel identifican recursos —`/patients/<id>`— y algunas son
 * directamente credenciales: `/receta/<token>`, `/check-in/<token>`,
 * `/presupuesto/<token>`, `/derivacion/<token>` y `/lista-espera/<token>` son
 * enlaces al portador, y quien tiene la dirección tiene el acceso. Mandarlas a
 * un tercero no sería filtrar una métrica: sería repartir las llaves.
 *
 * Por eso de cada pantalla sale el **molde** de la ruta y nunca la ruta real.
 * `/patients/$patientId` viaja como `/patients/:patientId`, que responde "se
 * usó la ficha de un paciente" sin decir de cuál.
 */

/** El último molde de ruta conocido, con el que se corrige lo que PostHog agrega solo. */
let rutaActual = "/desconocido";
let pendientes: Array<[string, Record<string, unknown>]> | null = null;

/**
 * Un tramo de ruta que se puede publicar.
 *
 * Las rutas del panel son palabras (`patients`, `waiting-room`, `settings`), y
 * los parámetros llegan como `$patientId` porque el molde de TanStack los
 * escribe así. Todo lo demás —un identificador, un token, un tramo que no
 * reconozco— sale como `:id`.
 *
 * La lista es al revés de lo habitual: en vez de enumerar lo que hay que
 * tapar, enumera lo único que se deja pasar. Una ruta nueva que alguien
 * agregue el año que viene no necesita acordarse de esta decisión para no
 * filtrar: si no parece una palabra, ya está tapada.
 */
export function tramoSeguro(tramo: string): string {
  if (tramo.startsWith("$")) return `:${tramo.slice(1)}`;
  return /^[a-z][a-z-]{0,23}$/.test(tramo) ? tramo : ":id";
}

/** El molde de una ruta, sin identificadores ni consulta ni fragmento. */
export function rutaSegura(molde: unknown): string {
  if (typeof molde !== "string" || !molde.startsWith("/")) return "/desconocido";
  // `split` devuelve `string | undefined` en cada posición bajo
  // `noUncheckedIndexedAccess`, y un molde sin consulta ni fragmento pasa
  // igual por acá: el `?? ""` es el caso imposible, no el habitual.
  const soloRuta = molde.split("?")[0]?.split("#")[0] ?? "";
  const tramos = soloRuta.split("/").filter(Boolean);
  if (tramos.length === 0) return "/";
  return `/${tramos.map(tramoSeguro).join("/")}`;
}

/**
 * Lo que PostHog adjunta por su cuenta y acá no puede viajar.
 *
 * `posthog-js` agrega `$current_url` y `$pathname` a cada evento leyéndolos
 * del navegador, así que mandar un nombre de pantalla prolijo no alcanza: la
 * dirección real viajaría igual, en otra propiedad. Se reescriben con el molde.
 *
 * Los referentes se van del todo. Dentro del panel, el referente de una
 * pantalla es la pantalla anterior, que puede ser la ficha de un paciente; y
 * de dónde viene alguien que ya inició sesión no es una pregunta que este
 * producto necesite responder.
 */
export function limpiarPropiedades(
  propiedades: Record<string, unknown>,
): Record<string, unknown> {
  const limpias = { ...propiedades };
  const origen =
    typeof window === "undefined" ? "" : (window.location?.origin ?? "");
  if ("$current_url" in limpias) limpias.$current_url = `${origen}${rutaActual}`;
  if ("$pathname" in limpias) limpias.$pathname = rutaActual;
  for (const clave of [
    "$referrer",
    "$referring_domain",
    "$initial_referrer",
    "$initial_referring_domain",
    "$initial_current_url",
    "$initial_pathname",
    "title",
  ]) {
    delete limpias[clave];
  }
  return limpias;
}

function cargar(): void {
  if (!POSTHOG_KEY || typeof window === "undefined" || window.posthog) return;

  pendientes = [];
  const script = document.createElement("script");
  script.async = true;
  script.src = `${POSTHOG_HOST}/static/array.js`;
  script.dataset.rondaPosthog = "true";
  script.addEventListener("load", () => {
    window.posthog?.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Las pantallas las manda `verPantalla`, ya convertidas a molde. La
      // automática mandaría la dirección real en el primer evento, antes de
      // que nadie pueda corregirla.
      capture_pageview: false,
      // La captura automática lee el texto y los atributos de lo que se
      // clickea. En el panel eso es el nombre de un paciente en una fila, el
      // título de una receta, el asunto de un mensaje.
      autocapture: false,
      // La repetición de sesión filma la pantalla, y acá la pantalla es una
      // historia clínica.
      disable_session_recording: true,
      sanitize_properties: limpiarPropiedades,
    });
    for (const [nombre, propiedades] of pendientes ?? []) {
      window.posthog?.capture(nombre, propiedades);
    }
    pendientes = null;
  });
  document.head.appendChild(script);
}

/** Registra una pantalla por su molde de ruta. */
export function verPantalla(molde: unknown): boolean {
  if (!POSTHOG_KEY) return false;
  rutaActual = rutaSegura(molde);
  cargar();
  const evento: [string, Record<string, unknown>] = [
    "$pageview",
    { ruta: rutaActual },
  ];
  if (pendientes) pendientes.push(evento);
  else if (window.posthog) window.posthog.capture(...evento);
  else return false;
  return true;
}

/**
 * Lo mínimo del router para saber qué pantalla se abrió.
 *
 * Se describe por forma y no se importa el router: así el archivo se puede
 * probar con un doble, y no queda un ciclo entre el router y la medición.
 */
type RouterObservable = {
  state: { matches: ReadonlyArray<{ fullPath?: unknown; routeId?: unknown }> };
  subscribe: (evento: "onResolved", escucha: () => void) => () => void;
};

/** El molde de la pantalla abierta: la coincidencia más profunda del router. */
export function moldeActual(router: RouterObservable): unknown {
  const coincidencias = router.state.matches;
  const ultima = coincidencias[coincidencias.length - 1];
  return ultima?.fullPath ?? ultima?.routeId;
}

/**
 * Engancha la medición a la navegación.
 *
 * Devuelve la función para desengancharla, que es lo que espera quien prueba
 * esto; en la aplicación vive lo que vive la pestaña.
 */
export function medirNavegacion(router: RouterObservable): () => void {
  if (!POSTHOG_KEY) return () => {};
  // La suscripción va antes que la primera pantalla: si el router todavía no
  // resolvió nada, `moldeActual` no devuelve un molde y mandar igual sería
  // anotar una visita a `/desconocido` en cada arranque. La resolución inicial
  // llega por `onResolved` como cualquier otra navegación.
  const desenganchar = router.subscribe("onResolved", () => {
    verPantalla(moldeActual(router));
  });
  const inicial = moldeActual(router);
  if (typeof inicial === "string") verPantalla(inicial);
  return desenganchar;
}
